// React hooks and routing
import { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate } from 'react-router-dom';

// UI Components and Layout
import LandingLayout from "@/components/LandingLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import RotatingBorderLabel from "@/components/ui/rotatingBorderLabel";
import {GalleryCard, WideGalleryCard } from "@/components/ui/cardGallery";
import { ScrollImageFader } from "@/components/ui/scroll-area";
import PricingLayout from '@/components/ui/pricing';
import KanbanSkeleton from '@/components/ui/KanbanSkeleton';
import Testimonials from '@/components/Testimonials';

// Custom Components
import ChangelogMarquee from "@/components/MarqueeChangeLog";
import ScrollKanbanBlock from "@/components/KanbanScrollBlock";

// Animations and Theme
import { motion } from "framer-motion";
import { useTheme } from "@/components/theme-provider";

// Icons
import {
  Bot,
  Brain,
  Check,
  Command,
  Kanban,
  Circle,
  MessageSquareCode,
  MousePointerClick,
  Sparkles, 
  Zap,
  Layout,
  Play,
  Pause
} from "lucide-react";
import { TbServer } from "react-icons/tb";

// Assets - Icons and SVGs
import StatusBotIcon from "@/assets/status-bot-icon.svg";
import StatusBotIconVivid from "@/assets/statusBotIcon-vivid.svg";
import StatusBotOutlineIcon from '@/components/StatusBotIcon';
// import sbMonitorDark from "@/assets/sbMonitor.svg";
// import sbKanbanDark from "@/assets/sbKanban.svg";
// import sbDashboardDark from "@/assets/sbDashboard.svg";
// import sbMonitorLight from "@/assets/sbMonitorLight.svg";
// import sbKanbanLight from "@/assets/sbKanbanLight.svg";
// import sbDashboardLight from "@/assets/sbDashboardLight.svg";

import sbMonitor from "@/assets/sbMonitor.svg";
import sbKanban from "@/assets/sbKanban.svg";
import sbDashboard from "@/assets/sbDashboard.svg";

// Assets - Screenshots
import heroMonitorScreenshot from "@/assets/statusbot-monitor-details-new.png";
import heroPlanScreenshot from "@/assets/statusbot-plan-board-2025.png";
import sbSSPromptDark from "@/assets/screenshots/sb_SS_promptDark.png";
import sbSSPromptLight from "@/assets/screenshots/sb_SS_promptLight.png";
import sbSSMonitorDark from "@/assets/screenshots/sb_SS_monitorDark.png";
import sbSSMonitorLight from "@/assets/screenshots/sb_SS_monitorLight.png";
import sbSSKanbanDark from "@/assets/screenshots/sb_SS_plannerDark.png";
import sbSSKanbanLight from "@/assets/screenshots/sb_SS_plannerLight.png";
import sbSSFormDark from "@/assets/screenshots/sb_SS_formDark.png";
import sbSSFormLight from "@/assets/screenshots/sb_SS_formLight.png";
import sbSSFormExample from "@/assets/screenshots/sb_SS_FormExample.png";
import sbSSNewTaskDark from "@/assets/screenshots/sb_SS_NewTaskDark.png";
import sbSSNewTaskLight from "@/assets/screenshots/sb_SS_NewTaskLight.png";
import sbSSBrowserAgent1Dark from "@/assets/screenshots/sb_SS_browserAgent.png";
import sbSSBrowserAgent2Dark from "@/assets/screenshots/sb_SS_browserAgent2.png";
import sbSSBrowserAgent3Dark from "@/assets/screenshots/sb_SS_browserAgent3.png";
import sbSSBrowserAgent3Light from "@/assets/screenshots/sb_SS_browserAgent3Light.png";
import sbHomePageVideo from "@/assets/screenshots/ssAnimationAssets_slower.mp4";

// External Data
import { faqsExtra } from '@/pages/Support';



const features = [
  {
    icon: <Command className="w-8 h-8 text-violet-500" />,
    title: "Command Bar AI",
    description: "Access AI assistance anywhere with CMD+K. Get instant answers, create tasks, and analyze projects effortlessly."
  },
  {
    icon: <Brain className="w-8 h-8 text-violet-500" />,
    title: "AI Task Analysis",
    description: "Let AI help break down complex tasks, suggest improvements, and identify potential blockers before they happen."
  },
  {
    icon: <Kanban className="w-8 h-8 text-violet-500" />,
    title: "Flexible Project Views",
    description: "Switch seamlessly between board and list views. Organize tasks your way with custom filters and grouping."
  },
  {
    icon: <Bot className="w-8 h-8 text-violet-500" />,
    title: "Service Monitoring",
    description: "Keep your services healthy with AI-powered monitoring. Get intelligent alerts and predictive insights."
  },
  {
    icon: <MessageSquareCode className="w-8 h-8 text-violet-500" />,
    title: "Smart Planning",
    description: "AI-powered insights for weekly planning, task estimation, and resource allocation."
  },
  {
    icon: <Zap className="w-8 h-8 text-violet-500" />,
    title: "Lightning Fast",
    description: "Blazingly fast interface with keyboard shortcuts and instant search for maximum productivity."
  }
];

const rotatingTextsConfig = [
  { text: "Project Planning", color: "text-violet-500", cursorColor: "bg-violet-500" },
  { text: "Service Monitoring", color: "text-emerald-500", cursorColor: "bg-emerald-500" },
  { text: "Team Collaboration", color: "text-blue-500", cursorColor: "bg-blue-500" },
  { text: "Task Tracking", color: "text-orange-500", cursorColor: "bg-orange-500" },
];

const sectionYPadding = "py-12 sm:py-20";

const Landing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [showCommandDemo, setShowCommandDemo] = useState(false);
  const [commandDemoTextIndex, setCommandDemoTextIndex] = useState(0);
  const { theme } = useTheme();
  const lightTheme = theme === "light";

  // Video overlay state
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);
  const [showVideoControls, setShowVideoControls] = useState(false);
  const mouseMovementTimeoutRef = useRef(null);

  useEffect(() => {
    const text = rotatingTextsConfig[currentTextIndex].text;
    let timeouts = [];

    const animateText = async () => {
      setIsTyping(true);

      // Clear existing text
      setDisplayText('');

      // Type out the text
      for (let i = 0; i <= text.length; i++) {
        const timeout = setTimeout(() => {
          setDisplayText(text.slice(0, i));
        }, i * 100); // Type speed
        timeouts.push(timeout);
      }

      // Pause at the end
      const pauseTimeout = setTimeout(() => {
        setIsTyping(false);
        // Delete text
        for (let i = text.length; i >= 0; i--) {
          const timeout = setTimeout(() => {
            setDisplayText(text.slice(0, i));
          }, (text.length - i) * 50); // Delete speed
          timeouts.push(timeout);
        }

        // Move to next text
        const nextTimeout = setTimeout(() => {
          setCurrentTextIndex((prev) => (prev + 1) % rotatingTextsConfig.length);
        }, text.length * 50 + 100);
        timeouts.push(nextTimeout);
      }, text.length * 100 + 2000); // Wait time after typing

      timeouts.push(pauseTimeout);
    };

    animateText();

    // Cleanup function
    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, [currentTextIndex]);

  useEffect(() => {
    if (location.state?.scrollTo) {
      const element = document.getElementById(location.state.scrollTo);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location.state]);

  // StatusBot Speech Bubble Texts
  const commandDemoTexts = [
    "I'll help you with anything you need!",
    "Ask me anything about your project.",
    "I can create tasks with natural language.",
    "I can get insights on team performance.",
    "I can monitor your services in real-time.",
    "Let's get started!"
  ];

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        if (!showCommandDemo) {
          setShowCommandDemo(true);
          setCommandDemoTextIndex(0);
        } else {
          setCommandDemoTextIndex(prev => (prev + 1) % commandDemoTexts.length);
        }
      }
    };

    const handleKeyUp = (event) => {
      if (event.key === 'Escape') {
        setShowCommandDemo(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [showCommandDemo]);

  // const imgRef = useRef(null);
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);

  // Video overlay controls
  const handleVideoPlayPause = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      if (isVideoPlaying) {
        video.pause();
        setIsVideoPlaying(false);
      } else {
        video.play();
        setIsVideoPlaying(true);
      }
    }
  }, [isVideoPlaying]);

  const handleMouseMovement = useCallback(() => {
    setShowVideoControls(true);
    
    // Clear existing timeout
    if (mouseMovementTimeoutRef.current) {
      clearTimeout(mouseMovementTimeoutRef.current);
    }
    
    // Set new timeout to hide controls after 3 seconds of no movement
    mouseMovementTimeoutRef.current = setTimeout(() => {
      setShowVideoControls(false);
    }, 3000);
  }, []);

  const handleMouseEnter = useCallback(() => {
    setShowVideoControls(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setShowVideoControls(false);
    if (mouseMovementTimeoutRef.current) {
      clearTimeout(mouseMovementTimeoutRef.current);
    }
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (mouseMovementTimeoutRef.current) {
        clearTimeout(mouseMovementTimeoutRef.current);
      }
    };
  }, []);

  // Video Scroll Effect
  useEffect(() => {
    const el = videoRef.current;
    if (el) {
      const handleScroll = () => {
        const scrollY = window.scrollY;
        const maxScroll = 400;
        const degreeChange = 35;
        const scrollChange = Math.max(0, degreeChange - (scrollY / maxScroll) * degreeChange);
        el.style.transform = `perspective(1500px) rotate3d(1, 0, 0, ${scrollChange}deg) `;
      };

      // Set initial transform
      el.style.transformOrigin = 'center top';
      el.style.transition = 'transform 0.25s ease-out';

      // Add scroll listener
      handleScroll(); // Trigger on load
      window.addEventListener('scroll', handleScroll);

      // Cleanup function
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);


  return (
    <LandingLayout>
      <motion.div
        className="fixed bottom-5 right-5 z-40 bg-transparent p-4 flex items-center gap-3"
        initial={{ opacity: 0, y: 20 }}
        animate={{ 
          opacity: showCommandDemo ? 1 : 0,
          y: showCommandDemo ? 0 : 20
        }}
        transition={{ duration: 0.2 }}
        style={{ pointerEvents: showCommandDemo ? 'auto' : 'none' }}
      >
        <RotatingBorderLabel duration={4} borderWidth={2}>
        <motion.div
          className="relative bg-white dark:bg-zinc-800 px-5 py-2 rounded-full"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ 
            opacity: showCommandDemo ? 1 : 0,
            scale: showCommandDemo ? 1 : 0.8
          }}
          transition={{ duration: 0.3, delay: 0.1 }}
          key={commandDemoTextIndex}
        >
          <motion.span 
            className="text-sm font-medium text-gray-800 dark:text-gray-200 whitespace-nowrap text-center"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            {commandDemoTexts[commandDemoTextIndex]}
          </motion.span>
        </motion.div>
        </RotatingBorderLabel>
        <img src={StatusBotIconVivid} alt="Status Bot Icon" className="w-16 h-16" />
      </motion.div>

      {/* Hero Section */}
      <section className="px-0 py-4 sm:py-6 md:py-8 relative z-10 overflow-x-hidden">
        <motion.div 
          className="sm:container lg:max-w-none mx-auto text-center z-10 relative"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <Card className="pt-8 sm:pt-16 md:pt-20 pb-4 sm:pb-6 md:pb-8 px-0 sm:px-4 md:px-8 border-0">
            <CardContent className="space-y-4 sm:space-y-6 md:space-y-8">
              {/* Badge */}
              <motion.div 
                className="flex items-center justify-center z-10 relative"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
              >
                <span className="px-3 py-1 sm:px-4 sm:py-1.5 bg-violet-500/10 text-violet-500 rounded-full text-xs sm:text-sm font-medium flex items-center">
                  <Circle className="w-1 h-1 sm:w-1 sm:h-1 mr-1.5 sm:mr-2 opacity-75 fill-current" />
                  The AI-Driven Command Center
                  <Circle className="w-1 h-1 sm:w-1 sm:h-1 ml-1.5 sm:ml-2 opacity-75 fill-current" />
                </span>
              </motion.div>

              {/* Heading */}
              <div className="z-10 relative">
                <motion.div 
                  className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold tracking-tight space-y-0"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
                >
                  <div className="relative h-[1.5em] overflow-hidden inline-flex items-center">
                    <span className={`${rotatingTextsConfig[currentTextIndex].color}`}>
                      {displayText}
                      <span className={`inline-block w-[4px] sm:w-[6px] h-[1em] ${rotatingTextsConfig[currentTextIndex].cursorColor} animate-pulse ml-1`}>
                        &nbsp;
                      </span>
                    </span>
                  </div>
                </motion.div>
                <motion.div
                  className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold tracking-tight"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.7, ease: "easeOut" }}
                >
                  <span className="block sm:inline">with AI at Your Fingertips</span>
                </motion.div>
              </div>

              {/* Subheading */}
              <motion.p 
                className="text-xs sm:text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto z-10 relative"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.0, ease: "easeOut" }}
              >
                StatusBot is the AI-driven command center for software teams.  Daily insights and 24/7 monitoring to keep your team aligned and your site running flawlessly.
              </motion.p>

              {/* Buttons */}
              <motion.div 
                className="flex flex-col sm:flex-row gap-2 sm:gap-4 justify-center px-4 sm:px-0 z-10 relative"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.3, ease: "easeOut" }}
              >
                <Button size="default" sm:size="lg" className="px-10" onClick={() => navigate('/waitlist')}>
                  Join Waitlist
                </Button>
                {/* <Button size="default" sm:size="lg" variant="outline" onClick={() => navigate('/waitlist')}>
                  Schedule a demo
                </Button> */}
                {/* <Button size="default" sm:size="lg" variant="outline" onClick={() => navigate('/features')}>
                  See it in Action
                </Button> */}
              </motion.div>

              {/* Screenshot */}
              <motion.div 
                className="pt-4 sm:pt-8 md:pt-16 flex justify-center px-0 sm:px-2 md:px-8 z-0 relative"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.6, ease: "easeOut" }}
              >
                <div className="w-full">
                  {/* <img
                    ref={imgRef}
                    src={heroPlanScreenshot}
                    alt="Screenshot of the project planning interface"
                    className={`w-full rounded-lg border-2 border-violet-500/20 shadow-md shadow-violet-500/10 dark:shadow-violet-500/5
                      ${lightTheme 
                        ? 'invert hue-rotate-[-192deg] saturate-[4] brightness-110' 
                        : ''}`
                      }
                  /> */}
                  <div 
                    ref={videoContainerRef}
                    className="relative group cursor-pointer flex justify-center"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onMouseMove={handleMouseMovement}
                    onClick={handleVideoPlayPause}
                  >
                    <video
                      src={sbHomePageVideo}
                      ref={videoRef}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className={`w-full max-w-6xl rounded-lg border-2 border-violet-500/20 shadow-md shadow-violet-500/10 dark:shadow-violet-500/5`}
                    />
                    
                    {/* Video Control Overlay */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ 
                        opacity: showVideoControls ? 1 : 0
                      }}
                      transition={{ 
                        duration: 0.2,
                        ease: "easeInOut"
                      }}
                      className={`absolute inset-0 flex items-center justify-center pointer-events-none ${
                        showVideoControls ? 'pointer-events-auto' : 'pointer-events-none'
                      }`}
                    >
                      {/* Play/Pause Button */}
                      <motion.button
                        initial={{ scale: 0.8 }}
                        animate={{ 
                          scale: showVideoControls ? 1 : 0.8,
                          opacity: showVideoControls ? 1 : 0
                        }}
                        transition={{ 
                          duration: 0.2,
                          ease: "easeInOut"
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVideoPlayPause();
                        }}
                        className={`
                          relative p-4 rounded-full
                          bg-black/70 dark:bg-gray-900/80 
                          border border-white/20 dark:border-white/30
                          hover:bg-black/80 dark:hover:bg-gray-900/90
                          hover:scale-105
                          transition-all duration-150 ease-out
                          focus:outline-none focus:ring-2 focus:ring-violet-400/50
                          shadow-lg shadow-black/40 dark:shadow-black/60
                        `}
                        aria-label={isVideoPlaying ? "Pause video" : "Play video"}
                      >
                        <div className="w-8 h-8 flex items-center justify-center">
                          {isVideoPlaying ? (
                            <Pause className="w-8 h-8 text-white dark:text-gray-100 fill-current" />
                          ) : (
                            <Play className="w-8 h-8 text-white dark:text-gray-100 fill-current" />
                          )}
                        </div>
                      </motion.button>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </CardContent>
          </Card>
        </motion.div>
      </section>

      {/* Three Up Cards Section */}
      <section id="three-up-cards" className={`px-0 ${sectionYPadding} bg-violet-500/5`}>
        <motion.div className="container mx-auto">
          <div className="text-center mb-6 sm:mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-3">
              Clarity on Your Product, Instantly.
            </h2>
            <p className="text-lg sm:text-xl text-muted-foreground">
              See downtime alerts, blockers, and team progress — all in one place.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 py-3 sm:py-6">

          <GalleryCard
              backgroundImageUrl={sbMonitor}
              backgroundImageAlt="Real-time monitoring"
              title="Catch Issues Before Customers Do"
              subtitle="Real-time monitoring and AI-driven tests flag outages or broken flows the moment they happen — protecting revenue and brand trust."
              withGradient={false}
              fadeIn={true}
              cardNumberSequence={0}
              className="cursor-pointer"
            />
            <GalleryCard
              backgroundImageUrl={sbKanban}
              backgroundImageAlt="Team collaboration"
              title="One Source of Truth for Your Team"
              subtitle="StatusBot gives you a unified view of tasks, blockers, and updates from PMs, devs, and stakeholders — all in one place."
              withGradient={false}
              fadeIn={true}
              cardNumberSequence={1}
              className="cursor-pointer"
            />
            <GalleryCard
              backgroundImageUrl={sbDashboard}
              backgroundImageAlt="Decision making"
              title="Decisions at a Glance"
              subtitle="Ask simple questions like 'What's blocking release?' and get instant answers to lead your team with confidence."
              withGradient={false}
              fadeIn={true}
              cardNumberSequence={2}
              className="cursor-pointer"  
            />
          </div>
        </motion.div>
      </section>

      {/* Challenge/Solution Section with Full Height Background */}
      <section id="challenge-solution" className={`px-0 ${sectionYPadding}`}>
        <ScrollKanbanBlock 
          sections={[
            <div className="px-3 sm:px-6 lg:px-8">
              <div className="max-w-lg space-y-6">
                <div className="flex items-center">
                  <span className="px-3 py-1 sm:px-4 sm:py-1.5 bg-red-500/10 text-red-500 rounded-full text-xs sm:text-sm font-medium">
                    The Challenge
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                  Disorganization Costs Time and Money
                </h2>
                <p className="text-lg sm:text-xl text-gray-500 leading-relaxed">
                  Product owners spend too much time chasing updates — pinging developers, checking project boards, and pulling reports from multiple tools. Your product's health lives in scattered tools and endless Slack threads.
                </p>
                <p className="text-lg sm:text-xl text-gray-500 leading-relaxed">
                  Meanwhile, critical bugs slip through, downtime costs revenue, and stakeholders are left in the dark. The result? Frustration, delays, and wasted effort.
                </p>
              </div>
            </div>,
            <div className="px-3 sm:px-6 lg:px-8">
              <div className="max-w-lg space-y-6">
                <div className="flex items-center">
                  <span className="px-3 py-1 sm:px-4 sm:py-1.5 bg-violet-500/10 text-violet-500 rounded-full text-xs sm:text-sm font-medium">
                    The Solution
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                  The StatusBot Solution
                </h2>
                <p className="text-lg sm:text-xl text-gray-500 leading-relaxed">
                  Our AI-powered monitoring and testing agents keep eyes on your site 24/7, automatically raising tickets when issues appear.
                </p>
                <p className="text-lg sm:text-xl text-gray-500 leading-relaxed">
                  With an AI-driven project board, you can check progress, blockers, and risks with a simple question — giving you the confidence to make faster, smarter decisions and keep your business moving forward.
                </p>
                <div className="flex items-center">
                  <span className="px-3 py-1 sm:px-4 sm:py-1.5 bg-emerald-500/10 text-emerald-500 rounded-full text-xs sm:text-sm font-medium">
                    The Result
                  </span>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-center space-x-3">
                    <Check className="w-5 h-5 text-emerald-500" />
                    <span className="text-lg sm:text-xl text-gray-500 leading-relaxed">
                      <strong>Risks mitigated</strong>
                    </span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <Check className="w-5 h-5 text-emerald-500" />
                    <span className="text-lg sm:text-xl text-gray-500 leading-relaxed">
                      <strong>Confidence gained</strong>
                    </span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <Check className="w-5 h-5 text-emerald-500" />
                    <span className="text-lg sm:text-xl text-gray-500 leading-relaxed">
                      <strong>Time and profit boosted</strong>
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          ]}
          kanbanProps={{  
            colorScheme: "violet",
            columns: 3,
            items: 12
          }}
          initialColors={["slate", "violet", "blue", "emerald", "orange", "rose"]}
          endingColor="violet"
        />
      </section>


      {/* Monitoring Section — OLD But maybe rework and use */}
      {/* <section id="monitoring" className={`px-0 ${sectionYPadding} bg-violet-500/5`}>
        <motion.div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 sm:gap-12 items-center">
            <div className="space-y-8 lg:col-span-2">
              <div className="flex items-center">
                <span className="px-3 py-1 sm:px-4 sm:py-1.5 bg-violet-500/10 text-violet-500 rounded-full text-xs sm:text-sm font-medium flex items-center">
                  <TbServer className="w-4 h-4 mr-2" />
                  Integrated Monitoring
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold">
                Keep Your Services Healthy with AI-Powered Monitoring
              </h2>
              <p className="text-xl text-muted-foreground">
                Monitor your web services, APIs, and infrastructure in the same place you plan your projects. Get intelligent alerts and predictive insights before issues impact your users.
              </p>
              <ul className="space-y-4">
                {[
                  "Real-time monitoring of endpoints and services",
                  "AI-powered anomaly detection and predictive alerts",
                  "Automatic incident creation and task assignment",
                  "Historical performance analytics and trends",
                ].map((item, index) => (
                  <motion.li 
                    key={item} 
                    className="flex items-center space-x-3"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ 
                      duration: 0.6, 
                      delay: index * 0.1,
                      ease: [0.25, 0.46, 0.45, 0.94] 
                    }}
                    viewport={{ once: true, margin: "-50px" }}
                  >
                    <Sparkles className="w-5 h-5 text-violet-500" />
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
            <motion.div 
              className="lg:pl-8 lg:col-span-3"
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ 
                duration: 0.8, 
                ease: [0.25, 0.46, 0.45, 0.94] 
              }}
              viewport={{ once: true, margin: "-50px" }}
            >
              <img
                src={heroMonitorScreenshot}
                alt="Service monitoring dashboard"
                className={`rounded-lg shadow-xl border border-violet-500/30 shadow-violet-500/20 dark:shadow-violet-500/20 
                  ${lightTheme 
                    ? 'invert hue-rotate-[-192deg] saturate-[4] brightness-110' 
                    : ''}`
                  }
              />
            </motion.div>
          </div>
        </motion.div>
      </section> */}

      {/* Key Features Section */}
      <section id="key-features" className={`px-0 ${sectionYPadding}`}>
        <motion.div className="container mx-auto">
          <div className="text-center mb-6 sm:mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-3">
              Everything you need in one place
            </h2>
            <p className="text-lg sm:text-xl text-muted-foreground">
              Powerful features designed to keep your team organized and productive.
            </p>
          </div>
          <div className="space-y-20">
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.6, 
                delay: 0 * 0.1,
                ease: [0.25, 0.46, 0.45, 0.94] 
              }}
              viewport={{ once: true, margin: "-50px" }}
            >
              <WideGalleryCard
                image1Url={lightTheme ? sbSSPromptLight : sbSSPromptDark}
                textContent={
                  <div className="space-y-4">
                    <span className="inline-flex items-center space-x-2 px-3 py-1 bg-teal-500/10 text-teal-500 rounded-full text-xs font-medium">
                      <div className="relative">
                        <StatusBotOutlineIcon className="w-4 h-4" />
                      </div>
                      <span>Ai Powered Insights</span>
                    </span>
                    <h3 className="text-3xl md:text-4xl font-bold">Daily Overview</h3>
                    <p className="text-muted-foreground">
                      Stay in the know without extra meetings. Ask StatusBot for a quick daily rundown of your product's health, open tasks, and blockers — all in plain language.
                    </p>
                  </div>
                }
                image1Width="60%"
                textWidth="40%"
                order="text-image-image"
                bgIndex={1}
                className="hover:shadow-lg hover:shadow-violet-500/10 transition-all"
                imageStretch={true}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.6, 
                delay: 1 * 0.1,
                ease: [0.25, 0.46, 0.45, 0.94] 
              }}
              viewport={{ once: true, margin: "-50px" }}
            >
              <WideGalleryCard
                image1Url={lightTheme ? sbSSKanbanLight : sbSSKanbanDark}
                image1HoverUrl={lightTheme ? sbSSNewTaskLight : sbSSNewTaskDark}
                textContent={
                  <div className="space-y-4">
                    <span className="inline-flex items-center space-x-2 px-3 py-1 bg-blue-500/10 text-blue-500 rounded-full text-xs font-medium">
                      <div className="relative">
                        <Kanban className="w-4 h-4" />
                      </div>
                      <span>Project Management</span>
                    </span>
                    <h3 className="text-3xl md:text-4xl font-bold">Planner</h3>
                    <p className="text-muted-foreground">
                      Your team's lightweight board: built for developers, by developers. Track tasks, bugs, and releases without the complexity of traditional project management tools.
                    </p>
                  </div>
                }
                image1Width="60%"
                textWidth="40%"
                order="image-image-text"
                bgIndex={3}
                className="hover:shadow-lg hover:shadow-violet-500/10 transition-all"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.6, 
                delay: 2 * 0.1,
                ease: [0.25, 0.46, 0.45, 0.94] 
              }}
              viewport={{ once: true, margin: "-50px" }}
            >
              <WideGalleryCard
                image1Url={lightTheme ? sbSSMonitorLight : sbSSMonitorDark}
                textContent={
                  <div className="space-y-4">
                    <span className="inline-flex items-center space-x-2 px-3 py-1 bg-teal-500/10 text-teal-500 rounded-full text-xs font-medium">
                      <div className="relative">
                        <div className="w-2 h-2 bg-emerald-400 rounded-full" />
                        <div className="absolute inset-0 w-2 h-2 bg-emerald-400 rounded-full animate-ping" />
                      </div>
                      <span>All systems operational</span>
                    </span>
                    <h3 className="text-3xl md:text-4xl font-bold">Always-On Monitoring</h3>
                    <p className="text-muted-foreground">
                      Even when your team is offline, StatusBot's AI agents continuously test your site like real users — catching downtime and critical bugs before customers do.
                    </p>
                  </div>
                }
                image1Width="60%"
                textWidth="40%"
                order="text-image-image"
                bgIndex={5}
                className="hover:shadow-lg hover:shadow-violet-500/10 transition-all"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.6, 
                delay: 2 * 0.1,
                ease: [0.25, 0.46, 0.45, 0.94] 
              }}
              viewport={{ once: true, margin: "-50px" }}
            >
              <WideGalleryCard
                image1Url={lightTheme ? sbSSBrowserAgent3Light : sbSSBrowserAgent3Dark}
                textContent={
                  <div className="space-y-4">
                    <span className="inline-flex items-center space-x-2 px-3 py-1 bg-blue-500/10 text-blue-500 rounded-full text-xs font-medium">
                      <div className="relative">
                        <MousePointerClick className="w-4 h-4" />
                      </div>
                      <span>AI-Powered Automation</span>
                    </span>
                    <h3 className="text-3xl md:text-4xl font-bold">Browser Agents</h3>
                    <p className="text-muted-foreground">
                      StatusBot's AI agents continuously test your site like real users — catching downtime and critical bugs before customers do.
                    </p>
                  </div>
                }
                image1Width="55%"
                textWidth="40%"
                order="image-image-text"
                bgIndex={7}
                className="hover:shadow-lg hover:shadow-violet-500/10 transition-all"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.6, 
                delay: 3 * 0.1,
                ease: [0.25, 0.46, 0.45, 0.94] 
              }}
              viewport={{ once: true, margin: "-50px" }}
            >
              <WideGalleryCard
                image1Url={lightTheme ? sbSSFormExample : sbSSFormExample}
                // image2Url={sbSSFormExample}
                textContent={
                  <div className="space-y-4">
                    <span className="inline-flex items-center space-x-2 px-3 py-1 bg-teal-500/10 text-teal-500 rounded-full text-xs font-medium">
                      <div className="relative">
                        <Layout className="w-4 h-4" />
                      </div>
                      <span>Smart Forms</span>
                    </span>
                    <h3 className="text-3xl md:text-4xl font-bold">Smart Request Forms</h3>
                    <p className="text-muted-foreground">
                      Streamline outside requests with simple forms. StatusBot automatically de-duplicates tickets, so your backlog stays clean and your team stays focused.
                    </p>
                  </div>
                }
                image1Width="40%"
                textWidth="40%"
                order="text-image-image"
                bgIndex={9}
                className="hover:shadow-lg hover:shadow-violet-500/10 transition-all"
              />
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Features Grid */}
      {/* <section id="other-features" className={`px-0 ${sectionYPadding}`}>
        <motion.div className="container mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-left mb-6 sm:mb-8 md:mb-12">
            Project management that works for you
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8 antialiased">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.6, 
                  delay: index * 0.1, 
                  ease: [0.25, 0.46, 0.45, 0.94] 
                }}
                viewport={{ once: false, margin: "-50px" }}
                className="h-full"
              >
                <motion.div
                  whileHover={{ 
                    scale: 1.02,
                    y: -8,
                    transition: { type: "spring", stiffness: 300, damping: 20 }
                  }}
                  className="h-full cursor-pointer"
                >
                  <Card className="px-3 py-3 sm:p-6 h-full hover:shadow-lg hover:shadow-violet-500/10 transition-all border-2 border-violet-500/20 antialiased">
                    <CardContent className="p-0 space-y-2 sm:space-y-4 pt-2 sm:pt-4 antialiased">
                      {feature.icon}
                      <h3 className="text-lg sm:text-xl font-semibold antialiased">{feature.title}</h3>
                      <p className="text-sm sm:text-base text-muted-foreground antialiased">{feature.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section> */}

      {/* Pricing Section */}
      <section id="pricing" className={`px-0 ${sectionYPadding} bg-violet-500/5`}>
        <PricingLayout />
      </section>

      {/* Testimonials Section */}
      {/* <section id="testimonials" className={`px-0 ${sectionYPadding}`}>
        <Testimonials />
      </section> */}

      {/* FAQ Section */}
      <section id="faq" className={`px-0 ${sectionYPadding}`}>
        <div className="container mx-auto max-w-4xl">
        {/* FAQs */}
        <Card className="border-0 shadow-none">
          <CardHeader className="text-center mb-4 sm:mb-6 md:mb-8">
            <CardTitle className="text-2xl sm:text-3xl md:text-4xl font-bold">Frequently Asked Questions</CardTitle>
            <CardDescription>
              Quick answers to common questions about StatusBot
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full space-y-4">
              {faqsExtra.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger>{faq.question}</AccordionTrigger>
                  <AccordionContent>
                    <div className="whitespace-pre-line">{faq.answer}</div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>
        </div>
      </section>

      {/* Bottom Marquee Section */}
      <section className="w-full bg-background border-t">
        <div className="w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw]">
          <ChangelogMarquee />
        </div>
      </section>
    </LandingLayout>
  );
};

export default Landing;
